import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CreateIcon from '@mui/icons-material/Create';
import {
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import Button from '@shared/components/Buttons/Button';
import {
  RichTextEditor,
  createEmptyValue,
  stringifySlateValue,
  isContentEmpty,
  getCharacterCount,
  type CustomElement,
} from '@shared/components/RichTextEditor';
import { UserAvatar } from '@shared/components/UserAvatar';
import { UserContext } from '@/shared/contexts/UserContext';

const MAX_THREAD_LENGTH = 5000;

interface CreateThreadFormProps {
  onSubmit: (content: string, isSlateFormat: boolean) => Promise<void>;
  isCreating: boolean;
  companyName: string;
}

const FormContainer = styled(motion.div)`
  background: linear-gradient(135deg, #ffffff 0%, rgba(192, 90, 58, 0.02) 100%);
  border-radius: 16px;
  border: 1px solid rgba(192, 90, 58, 0.15);
  overflow: hidden;
  margin-bottom: 24px;
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(90deg, rgba(192, 90, 58, 0.05) 0%, rgba(192, 90, 58, 0.02) 100%);
  border-bottom: 1px solid rgba(192, 90, 58, 0.1);
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FormBody = styled.div`
  padding: 20px;
`;

const StartThreadButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  width: 100%;
  padding: 20px 24px;
  background: linear-gradient(135deg, #ffffff 0%, rgba(192, 90, 58, 0.03) 100%);
  border: 2px dashed rgba(192, 90, 58, 0.2);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 24px;

  &:hover {
    border-color: rgba(192, 90, 58, 0.4);
    background: linear-gradient(135deg, rgba(192, 90, 58, 0.03) 0%, rgba(192, 90, 58, 0.06) 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(192, 90, 58, 0.1);
  }
`;

const ButtonIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #c05a3a 0%, #2952a3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(192, 90, 58, 0.3);
`;

const ButtonText = styled.div`
  text-align: left;
`;

const ButtonTitle = styled(Typography)`
  font-weight: 600;
  font-size: 1rem;
  color: #2a2018;
`;

const ButtonSubtitle = styled(Typography)`
  font-size: 0.875rem;
  color: #6b7280;
`;

const UserSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const InputSection = styled.div`
  flex: 1;
`;

const EditorWrapper = styled.div`
  margin-bottom: 8px;
`;

const CharacterCounter = styled.div<{ $isOverLimit?: boolean }>`
  font-size: 0.75rem;
  color: ${({ $isOverLimit }) => ($isOverLimit ? '#ef4444' : '#9ca3af')};
  text-align: right;
  margin-top: 4px;
  font-weight: ${({ $isOverLimit }) => ($isOverLimit ? 600 : 400)};
`;

const CreateThreadForm: React.FC<CreateThreadFormProps> = ({
  onSubmit,
  isCreating,
  companyName,
}) => {
  const { user } = useContext(UserContext);
  const [isExpanded, setIsExpanded] = useState(false);
  const [editorValue, setEditorValue] = useState<CustomElement[]>(createEmptyValue());

  const isEmpty = isContentEmpty(editorValue);
  const charCount = getCharacterCount(editorValue);
  const isOverLimit = charCount > MAX_THREAD_LENGTH;

  const handleSubmit = async () => {
    if (isEmpty || isOverLimit) return;

    const content = stringifySlateValue(editorValue);
    await onSubmit(content, true);
    setEditorValue(createEmptyValue());
    setIsExpanded(false);
  };

  const handleCancel = () => {
    setEditorValue(createEmptyValue());
    setIsExpanded(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      if (!isEmpty && !isCreating && !isOverLimit) {
        event.preventDefault();
        handleSubmit();
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
      return;
    }
  };

  if (!isExpanded) {
    return (
      <StartThreadButton
        onClick={() => setIsExpanded(true)}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <ButtonIcon>
          <AddIcon />
        </ButtonIcon>
        <ButtonText>
          <ButtonTitle>Start a Thread</ButtonTitle>
          <ButtonSubtitle>Share insights or ask questions about {companyName}</ButtonSubtitle>
        </ButtonText>
      </StartThreadButton>
    );
  }

  return (
    <FormContainer
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <FormHeader>
        <HeaderTitle>
          <CreateIcon sx={{ color: '#c05a3a', fontSize: 22 }} />
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#2a2018' }}>
            New Thread
          </Typography>
        </HeaderTitle>
        <IconButton
          size="small"
          onClick={handleCancel}
          sx={{
            'color': '#9ca3af',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </FormHeader>

      <FormBody>
        <UserSection>
          <UserAvatar
            firstName={user?.firstName}
            lastName={user?.lastName}
            username={user?.username}
            avatarColor={user?.avatarColor || '#c05a3a'}
            size={44}
          />
          <InputSection>
            <EditorWrapper>
              <RichTextEditor
                value={editorValue}
                onChange={(value) => setEditorValue(value as CustomElement[])}
                placeholder={`What would you like to share about ${companyName}?`}
                disabled={isCreating}
                autoFocus
                preset="thread"
                minHeight={120}
                maxHeight={300}
                onKeyDown={handleKeyDown}
              />
              <CharacterCounter $isOverLimit={isOverLimit}>
                {charCount}/{MAX_THREAD_LENGTH}
              </CharacterCounter>
            </EditorWrapper>

            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                size="small"
                variantType="cancel"
                onClick={handleCancel}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                size="small"
                onClick={handleSubmit}
                disabled={isEmpty || isCreating || isOverLimit}
              >
                {isCreating ? 'Posting...' : 'Post Thread'}
              </Button>
            </Box>
          </InputSection>
        </UserSection>
      </FormBody>
    </FormContainer>
  );
};

export default CreateThreadForm;
